import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';
import { FormatService } from '../../services/format/format.service';
import * as moment from 'moment';

// Read-only monitoring of Tahfidz: hafalan (memorization targets + hafal
// marks) — same page anatomy as the tilawah page: KPI strip + segments
// (Pengguna | Target | Aktivitas). Muraja'ah lives on its own page
// (pages/murojaah).
@Component({
  selector: 'app-hafalan',
  templateUrl: './hafalan.component.html',
  styleUrls: ['./hafalan.component.css']
})
export class HafalanComponent implements OnInit {
  isView : string = 'users'; // users | targets | activity
  kpi : any = null;
  directoryFile : string = '';
  isPageSize : number = 10;

  // ===== targets =====
  isData : any = [];
  isTotalAll : number = 0;
  isKeyword : string = '';
  isMode : string = '';
  isStatus : string = '';
  isSort : string = 'newest';
  isPage : number = 1;
  isPages : any = [];
  isTableLoading : boolean = false;

  // ===== users =====
  users : any = [];
  usersTotal : number = 0;
  usersKeyword : string = '';
  usersSort : string = 'ayat';
  usersPage : number = 1;
  usersPages : any = [];
  usersLoading : boolean = false;

  // ===== activity =====
  act : any = [];
  actTotal : number = 0;
  actKeyword : string = '';
  actDays : string = '30';
  actPage : number = 1;
  actPages : any = [];
  actLoading : boolean = false;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    moment.locale('id');
    this.isPageSize = this.apiService.LIMIT || 10;
    this.loadSummary();
    this.getUsers();
  }

  setView(view : string){
    if(this.isView === view){ return; }
    this.isView = view;
    if(view === 'targets' && !this.isData.length && !this.isTableLoading){ this.getData(); }
    if(view === 'activity' && !this.act.length && !this.actLoading){ this.getActivity(); }
  }

  loadSummary(){
    this.apiService.methodGetSilent(`hafalan/summary`, this.authService.getToken()).subscribe((response) => {
      if(response && response.status == 200){ this.kpi = response.data; }
    });
  }
  completePct(){
    if(!this.kpi || !this.kpi.targets_total){ return 0; }
    return Math.round(this.kpi.targets_complete / this.kpi.targets_total * 100);
  }

  // ===== targets =====
  private searchTimer : any;
  private lastQuery : string = '';
  onKeywordInput(value : string){
    this.isKeyword = value;
    clearTimeout(this.searchTimer);
    // debounce: wait 450ms after the last keystroke before hitting the API
    this.searchTimer = setTimeout(() => this.applyFilter(), 450);
  }
  async applyFilter(){
    clearTimeout(this.searchTimer);
    const query = `${this.isKeyword}|${this.isMode}|${this.isStatus}|${this.isSort}`;
    if(query === this.lastQuery){ return; } // query unchanged, skip the request
    this.lastQuery = query;
    this.isPage = 1;
    await this.getData();
  }
  async getData(){
    const searchData = {"keyword": this.isKeyword, "mode": this.isMode, "status": this.isStatus, "sort": this.isSort};
    const offset = (this.isPage - 1) * this.isPageSize;
    this.isTableLoading = true;
    try{
      this.apiService.methodGetSilent(`hafalan/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
        this.isTableLoading = false;
        if(response && response.status == 200){
          this.directoryFile = response.directoryFile || '';
          this.isData = (response.data || []).map((v : any) => this.decorateTarget(v));
          this.isTotalAll = response.total || 0;
          this.isPages = this.pagesFor(this.isPage, this.totalPages());
        }
      }, () => { this.isTableLoading = false; });
    }catch(error){
      this.isTableLoading = false;
      console.log(error);
    }
  }
  // shared with the detail page's "Target lain" tab — keep in sync
  decorateTarget(v : any){
    v.userName = v.user_name || 'Akun tidak ditemukan';
    v.userPhotoUrl = this.photoUrl(v.user_photo, 'user/avatar/');
    v.createdVw = this.dayLabel(v.created_day) || (v.created_at ? moment(v.created_at).format('D MMM YYYY') : '-');
    v.targetVw = this.dayLabel(v.target_day);
    v.deadlineVw = v.target_day == null ? 'Tanpa tenggat'
      : v.remaining_days > 0 ? `${this.formatNumber(v.remaining_days)} hari lagi`
      : v.remaining_days === 0 ? 'Hari ini'
      : `Lewat ${this.formatNumber(-v.remaining_days)} hari`;
    v.durationVw = v.duration_days != null ? `${this.formatNumber(v.duration_days)} hari` : '—';
    v.lastProgressVw = v.last_progress_at ? moment(v.last_progress_at).fromNow() : 'Belum ada';
    v.lastProgressFull = v.last_progress_at ? moment(v.last_progress_at).format('D MMMM YYYY HH:mm') : '';
    return v;
  }
  // epoch-day integer (local days since 1970, see the app's hafalan_day.dart) → "12 Sep 2026"
  dayLabel(day : any){
    if(day == null){ return ''; }
    return moment.utc(Number(day) * 86400000).format('D MMM YYYY');
  }
  photoUrl(photo : any, folder : string){
    if(!photo){ return ''; }
    return String(photo).includes('http') ? photo : `${this.directoryFile}${folder}${photo}`;
  }
  statusLabel(s : string){
    if(s === 'complete'){ return 'Selesai'; }
    if(s === 'overdue'){ return 'Lewat tenggat'; }
    return 'Berjalan';
  }
  // the detail page is per USER — a target row opens its owner
  openDetail(v : any){ this.router.navigate(['/hafalan', v.user_id]); }
  openUser(u : any){ this.router.navigate(['/hafalan', u.id]); }

  totalPages(){ return Math.max(1, Math.ceil(this.isTotalAll / this.isPageSize)); }
  pagesFor(current : number, total : number){
    const pages: any[] = [];
    for(let p = 1; p <= total; p++){
      if(p == 1 || p == total || Math.abs(p - current) <= 1){
        pages.push(p);
      }else if(pages[pages.length - 1] != '...'){
        pages.push('...');
      }
    }
    return pages;
  }
  async goToPage(p: any){
    if(p < 1 || p > this.totalPages() || p == this.isPage){ return; }
    this.isPage = p;
    await this.getData();
  }
  rowNo(i: number){ return (this.isPage - 1) * this.isPageSize + i + 1; }
  summaryFrom(){ return this.isTotalAll ? (this.isPage - 1) * this.isPageSize + 1 : 0; }
  summaryTo(){ return (this.isPage - 1) * this.isPageSize + this.isData.length; }
  rangeFrom(page : number, total : number){ return total ? (page - 1) * this.isPageSize + 1 : 0; }
  rangeTo(page : number, len : number){ return (page - 1) * this.isPageSize + len; }
  pagesOf(total : number){ return Math.max(1, Math.ceil(total / this.isPageSize)); }
  formatNumber(value : any){ return this.formatService.dotFormat(value || 0); }

  // wrap keyword matches in <mark> for the list; input is HTML-escaped first
  highlight(text : any, keyword : string = this.isKeyword){
    const esc = String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const kw = (keyword || '').trim();
    if(!kw){ return esc; }
    const escapedKeyword = kw
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // neutralize regex metacharacters
    return esc.replace(new RegExp(`(${escapedKeyword})`, 'gi'), '<mark class="vl-hl">$1</mark>');
  }

  // ===== users =====
  private usersTimer : any;
  private usersLastQuery : string = '';
  onUsersKeywordInput(value : string){
    this.usersKeyword = value;
    clearTimeout(this.usersTimer);
    this.usersTimer = setTimeout(() => this.applyUsersFilter(), 450);
  }
  async applyUsersFilter(){
    clearTimeout(this.usersTimer);
    const query = `${this.usersKeyword}|${this.usersSort}`;
    if(query === this.usersLastQuery){ return; }
    this.usersLastQuery = query;
    this.usersPage = 1;
    await this.getUsers();
  }
  getUsers(){
    const searchData = {"keyword": this.usersKeyword, "sort": this.usersSort};
    const offset = (this.usersPage - 1) * this.isPageSize;
    this.usersLoading = true;
    this.apiService.methodGetSilent(`hafalan/users/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
      this.usersLoading = false;
      if(response && response.status == 200){
        this.directoryFile = response.directoryFile || this.directoryFile;
        this.users = (response.data || []).map((u : any) => {
          u.photoUrl = this.photoUrl(u.photo, 'user/avatar/');
          u.nameVw = u.name || 'Akun tidak ditemukan';
          u.lastProgressVw = u.last_progress_at ? moment(u.last_progress_at).fromNow() : 'Belum ada';
          u.lastProgressFull = u.last_progress_at ? moment(u.last_progress_at).format('D MMMM YYYY HH:mm') : '';
          u.firstTargetVw = u.first_target_at ? moment(u.first_target_at).format('D MMM YYYY') : '-';
          u.completePct = u.targets_total ? Math.round(u.targets_complete / u.targets_total * 100) : 0;
          return u;
        });
        this.usersTotal = response.total || 0;
        this.usersPages = this.pagesFor(this.usersPage, this.pagesOf(this.usersTotal));
      }
    }, () => { this.usersLoading = false; });
  }
  async goUsersPage(p : any){
    if(p < 1 || p > this.pagesOf(this.usersTotal) || p == this.usersPage){ return; }
    this.usersPage = p; this.getUsers();
  }

  // ===== activity =====
  private actTimer : any;
  private actLastQuery : string = '';
  onActKeywordInput(value : string){
    this.actKeyword = value;
    clearTimeout(this.actTimer);
    this.actTimer = setTimeout(() => this.applyActFilter(), 450);
  }
  async applyActFilter(){
    clearTimeout(this.actTimer);
    const query = `${this.actKeyword}|${this.actDays}`;
    if(query === this.actLastQuery){ return; }
    this.actLastQuery = query;
    this.actPage = 1;
    await this.getActivity();
  }
  getActivity(){
    const searchData = {"keyword": this.actKeyword, "days": this.actDays};
    const offset = (this.actPage - 1) * this.isPageSize;
    this.actLoading = true;
    this.apiService.methodGetSilent(`hafalan/activity/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
      this.actLoading = false;
      if(response && response.status == 200){
        this.directoryFile = response.directoryFile || this.directoryFile;
        this.act = (response.data || []).map((a : any) => this.decorateActivity(a));
        this.actTotal = response.total || 0;
        this.actPages = this.pagesFor(this.actPage, this.pagesOf(this.actTotal));
      }
    }, () => { this.actLoading = false; });
  }
  // shared with the detail page — keep in sync
  decorateActivity(a : any){
    a.userName = a.user_name || 'Akun tidak ditemukan';
    a.userPhotoUrl = this.photoUrl(a.user_photo, 'user/avatar/');
    a.surahName = a.surah_name || `Surat ${a.surah}`;
    a.rangeVw = a.ayah_from === a.ayah_to ? `ayat ${a.ayah_from}` : `ayat ${a.ayah_from}–${a.ayah_to}`;
    a.timeVw = a.at ? moment(a.at).fromNow() : '';
    a.timeFull = a.at ? moment(a.at).format('D MMMM YYYY HH:mm') : '';
    // the day's last mark, written out: "Kamis, 11 September 2026 · 12:34"
    a.dayVw = a.at ? moment(a.at).format('D MMM YYYY, HH:mm') : '';
    return a;
  }
  async goActPage(p : any){
    if(p < 1 || p > this.pagesOf(this.actTotal) || p == this.actPage){ return; }
    this.actPage = p; this.getActivity();
  }
}
